// Dossier magazine — CONDUIRE UNE MOTO EN HIVER
// Contenu original — Bruno · Équipe Code 2 Roues · 2026
import {
  Snowflake, Eye, AlertTriangle, Sparkles, Target, ShieldCheck,
  Lightbulb, Wind, Thermometer, Zap, Bike, Umbrella, Compass,
  HelpCircle, CheckSquare, Clock, Fuel, Sun,
} from 'lucide-react';

export const hiverMotoArticle = {
  slug: 'conduire-moto-hiver-conseils-securite',
  weekVolume: 44,
  weekYear: 2026,
  monthLabel: 'Novembre 2026',
  category: 'Conseils moto & sécurité routière',
  shortTitle: 'Rouler l\'hiver — Les bons réflexes',
  title: "Conduire une moto en hiver : les bons réflexes pour rouler en sécurité",
  kicker: '🏍️❄️ Dossier saisonnier · Bruno · Équipe Code 2 Roues',
  subtitle:
    "L'hiver ne signifie pas nécessairement ranger sa moto au garage. Mais entre <strong>le froid, la pluie, le brouillard, le manque de luminosité et les risques de verglas</strong>, la conduite d'un deux-roues demande davantage d'anticipation et de vigilance. Le secret pour rouler en hiver n'est pas de résister au froid, mais <em>d'adapter sa conduite aux conditions réelles de la route</em>.",
  heroImage: '/images/articles/hiver-moto-hero.jpg',
  heroVideo: '/videos/hero-hiver-moto.mp4',
  heroImageAlt: "Motard équipé pour l'hiver roulant sur une route de montagne dans des conditions hivernales — illustration pédagogique CODE 2 ROUES.",
  topRightKicker: { line1: 'Conseils & sécurité', line2: 'Hiver · Froid · Adhérence' },
  readTimeMin: 10,
  publishedAt: '2026-11-02',
  seoDescription:
    "Froid, pluie, brouillard, verglas : découvrez les conseils Code2Roues pour préparer votre moto, adapter votre conduite et rouler plus sûrement en hiver.",
  seoKeywords:
    'conduite moto en hiver, équipement moto hiver, rouler à moto sous la pluie, verglas moto, sécurité motard, pneus moto hiver, brouillard moto',
  about: "Dossier saisonnier — rouler à moto l'hiver en sécurité : préparation, équipement, techniques et prudence.",
  chapo: {
    kicker: "L'idée à retenir",
    body:
      "Équipement, pneumatiques, freinage, trajectoires et visibilité : <strong>la sécurité commence avant le départ</strong>. En hiver, mieux vaut préparer chaque trajet, adapter son allure aux conditions réelles — et savoir reporter sa sortie quand les conditions deviennent dangereuses.",
    sideStat: {
      kicker: "La formule CODE 2 ROUES",
      big: 'PRÉPARER,\nANTICIPER,\nADAPTER',
      body: "L'hiver récompense ceux qui préparent chaque sortie : <strong>moto, équipement, trajet, météo</strong>. Rien ne s'improvise entre novembre et mars.",
      accent: '#0ea5e9',
    },
  },
  pullQuote: {
    text: "Un motard expérimenté n'est pas celui qui roule quelles que soient les conditions. C'est celui qui sait évaluer les risques et <strong>adapter sa décision</strong>.",
    source: 'Bruno · Équipe Code 2 Roues',
  },
  sections: [
    {
      part: 'Partie 1 — Préparation',
      title: "Avant de partir : préparez votre moto et votre trajet",
      intro:
        "En hiver, <strong>les conditions météorologiques peuvent évoluer rapidement</strong>. Une route simplement humide au départ peut devenir glissante quelques kilomètres plus loin, notamment dans les zones ombragées ou en altitude. Avant chaque déplacement, prenez quelques minutes pour vérifier les conditions de circulation.",
      inlineImage: {
        src: '/images/articles/hiver/03_pneumatiques.jpg',
        alt: "Vérification des pneumatiques moto en hiver — pression à froid, sculpture, usure, adaptation aux conditions.",
        caption: '🏍️ Pneumatiques : le seul contact entre votre moto et la chaussée',
        testid: 'hiver-pneus',
      },
      body:
        "<p><strong>Consultez la météo et l'état des routes.</strong> Vérifiez les températures prévues, les précipitations, le vent et les éventuelles alertes météo. En montagne, les conditions peuvent changer très rapidement. Soyez particulièrement attentif aux températures proches de 0 °C, surtout après une nuit froide ou en présence d'humidité. Les ponts, les sous-bois, les zones ombragées et les routes peu fréquentées peuvent présenter un risque accru de verglas.</p><p><strong>Contrôlez les pneumatiques.</strong> Les pneus constituent le seul contact entre votre moto et la chaussée. Vérifiez la pression à froid, l'état général (coupures, déformations), la profondeur des sculptures, l'adaptation aux conditions. Un pneu en bon état ne supprime pas les risques liés au froid — l'adhérence peut diminuer brutalement lorsque la chaussée devient glissante.</p>",
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Le bon réflexe :</strong> si du verglas est probable, si la chaussée est enneigée ou si les conditions ne permettent pas de circuler en sécurité, <em>reportez votre trajet</em>.",
        },
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          body: "Vérifiez également le <strong>bon fonctionnement des feux, clignotants, freins et commandes</strong>. Votre visière doit être propre et votre écran antibuée efficace.",
        },
      ],
    },
    {
      part: 'Partie 2 — L\'équipement',
      title: "Bien s'équiper : rester chaud sans perdre en mobilité",
      intro:
        "Le froid n'est pas seulement une question de confort. Il peut <strong>diminuer la sensibilité des mains, gêner les mouvements et augmenter la fatigue</strong>. Un équipement adapté doit protéger du froid et de l'humidité tout en préservant la liberté de mouvement nécessaire au pilotage.",
      inlineImage: {
        src: '/images/articles/hiver/02_equipement.jpg',
        alt: "Motard entièrement équipé pour l'hiver — casque intégral, blouson avec protections, gants moto hiver.",
        caption: "🧤 L'équipement complet : casque, blouson, gants hiver — pas de compromis",
        testid: 'hiver-equipement',
      },
      orderedList: [
        "<strong>Protégez les extrémités.</strong> Les mains et les pieds sont particulièrement exposés au froid. Privilégiez des gants moto adaptés à la saison, suffisamment isolants et imperméables, sans compromettre la préhension des poignées ni l'utilisation des commandes. Les poignées chauffantes et les protège-mains peuvent améliorer le confort — ils ne remplacent toutefois pas des gants appropriés. Pour les pieds, choisissez des bottes moto protectrices, adaptées aux températures et aux conditions humides.",
        "<strong>Adoptez le principe des couches.</strong> Une première couche respirante pour évacuer la transpiration, une couche isolante pour conserver la chaleur, une couche extérieure protectrice contre le vent et la pluie. Une tenue trop serrée peut gêner les mouvements. À l'inverse, des vêtements trop amples peuvent flotter au vent et perturber le confort de conduite.",
        "<strong>Protégez votre visage et votre visibilité.</strong> Un tour de cou adapté, une visière propre et un système antibuée efficace contribuent au confort et à la visibilité. Attention à la buée : elle peut apparaître rapidement lors des variations de température. <em>Ne poursuivez pas votre trajet avec une visibilité insuffisante.</em>",
      ],
      callouts: [
        {
          icon: Zap,
          accent: '#f59e0b',
          body: "<strong>Les équipements chauffants</strong> (gants, veste, poignées) sont une vraie révolution pour les trajets quotidiens. Prévoir un harnais électrique bien dimensionné sur la batterie — surtout si vous ajoutez poignées + gants + veste simultanément.",
        },
      ],
    },
    {
      part: 'Partie 3 — La conduite',
      title: 'Adapter sa conduite : souplesse et anticipation',
      intro:
        "En hiver, la chaussée peut offrir une adhérence très variable. Il faut donc <strong>éviter les gestes brusques et laisser davantage de marge à la moto</strong>.",
      inlineImage: {
        src: '/images/articles/hiver/05_distance_securite.jpg',
        alt: "Deux motos sur route humide avec symboles de distance de sécurité — la distance d'arrêt s'allonge sur chaussée mouillée.",
        caption: '📏 Augmentez systématiquement vos distances de sécurité',
        testid: 'hiver-distance',
      },
      orderedList: [
        "<strong>Réduisez votre vitesse.</strong> La vitesse doit être adaptée à l'adhérence, à la visibilité, au trafic et à l'état de la route, même lorsque la limitation autorisée est plus élevée. Une vitesse modérée permet de disposer de davantage de temps pour observer, anticiper et réagir.",
        "<strong>Augmentez les distances de sécurité.</strong> Sur chaussée humide ou glissante, la distance nécessaire pour s'arrêter peut augmenter. Gardez une marge suffisante avec le véhicule qui vous précède. Cette distance supplémentaire vous laisse davantage de temps pour anticiper un ralentissement ou un freinage imprévu.",
        "<strong>Évitez les actions brusques.</strong> Privilégiez des actions progressives sur l'accélérateur, les freins et les commandes. Évitez les accélérations soudaines, les freinages tardifs et les changements de direction trop rapides. L'objectif est de conserver une conduite régulière et de ne pas solliciter inutilement l'adhérence disponible.",
      ],
    },
    {
      part: 'Partie 4 — Le freinage',
      title: 'Freiner à moto en hiver : privilégier la progressivité',
      intro:
        "Le freinage demande une attention particulière lorsque la chaussée est froide, humide ou contaminée par des dépôts. Sur une surface glissante, <strong>une action trop brutale peut provoquer une perte d'adhérence</strong>.",
      inlineImage: {
        src: '/images/articles/hiver/06_freinage.jpg',
        alt: "Gros plan sur un gant moto et un levier de frein avec flèche rouge — illustration du geste progressif de freinage sur route humide.",
        caption: '🛑 Freiner progressivement, avec les deux freins',
        testid: 'hiver-freinage',
      },
      body:
        "<p>Pour limiter les risques :</p><ul><li>Anticipez les ralentissements et les intersections.</li><li>Commencez à freiner suffisamment tôt.</li><li>Augmentez progressivement la pression sur les freins.</li><li>Évitez les changements brusques de direction pendant un freinage.</li><li>Conservez une posture équilibrée et des bras souples.</li></ul>",
      callouts: [
        {
          icon: Lightbulb,
          accent: '#f59e0b',
          body: "<strong>À retenir :</strong> en hiver, <em>anticiper le freinage est préférable à devoir réagir dans l'urgence</em>. L'ABS peut aider à limiter le blocage des roues lors d'un freinage appuyé, mais il ne supprime pas les limites d'adhérence et ne garantit pas une distance d'arrêt courte sur une surface glissante.",
        },
      ],
    },
    {
      part: 'Partie 5 — Les virages',
      title: 'Virages et trajectoires : conserver une marge de sécurité',
      intro:
        "Un virage demande déjà une bonne coordination du regard, de la vitesse, de la posture et des actions sur le guidon. <strong>Lorsque l'adhérence diminue, la marge d'erreur se réduit.</strong>",
      inlineImage: {
        src: '/images/articles/hiver/07_virage.jpg',
        alt: "Motard dans un virage de route humide d'automne — illustration de la vigilance accrue nécessaire sur chaussée glissante.",
        caption: '🏔️ Adapter la trajectoire aux surfaces à faible adhérence',
        testid: 'hiver-virage',
      },
      body:
        "<p>Avant d'aborder un virage :</p><ul><li>Adaptez votre vitesse suffisamment tôt.</li><li>Regardez vers la trajectoire souhaitée.</li><li>Identifiez les éventuelles zones humides, gravillonnées ou ombragées.</li><li>Évitez les gestes brusques et les corrections inutiles.</li><li>Conservez une marge de sécurité adaptée à la visibilité et à l'état de la chaussée.</li></ul>",
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Soyez particulièrement prudent</strong> sur les plaques métalliques, les marquages au sol, les feuilles mortes et les zones couvertes de boue ou de gravillons lorsqu'ils sont humides. Ces surfaces peuvent être plus glissantes que l'asphalte environnant.",
        },
      ],
    },
    {
      part: 'Partie 6 — Les conditions',
      title: 'Pluie, brouillard, vent et verglas : 4 situations à anticiper',
      intro:
        "Chaque condition hivernale apporte ses propres pièges. Les reconnaître à l'avance permet d'adapter sa décision de rouler… ou de reporter.",
      imageCards: [
        {
          img: '/images/articles/hiver/04_route_hivernale.jpg',
          alt: 'Vue depuis le guidon sur route de montagne humide en hiver — anticipation et vigilance accrue.',
          numero: '🌧️',
          titre: 'Sous la pluie',
          description: 'Adhérence et visibilité réduites. Soyez vigilant au début d\'une averse — l\'eau remonte les dépôts.',
        },
        {
          img: '/images/articles/hiver/08_brouillard.jpg',
          alt: 'Motard dans le brouillard avec phares allumés — visibilité très réduite.',
          numero: '🌫️',
          titre: 'Dans le brouillard',
          description: 'Visibilité et évaluation des distances compliquées. Adaptez votre vitesse à la distance d\'observation.',
        },
        {
          img: '/images/articles/hiver/09_verglas.jpg',
          alt: 'Route verglacée avec panneau d\'alerte au verglas — le piège invisible de l\'hiver.',
          numero: '❄️',
          titre: 'En verglas / neige',
          description: 'Fine couche de glace souvent indétectable. La bonne décision : ne pas poursuivre à moto.',
        },
        {
          img: '/images/articles/hiver/07_virage.jpg',
          alt: 'Motard inclinant la moto dans un virage de route humide — vent latéral et surface instable.',
          numero: '💨',
          titre: 'Par vent fort',
          description: 'Rafales latérales sur ponts, viaducs, dépassements de poids-lourds. Marge latérale et vigilance.',
        },
      ],
      imageCardsCols: 4,
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body: "<strong>Si la chaussée est verglacée ou enneigée, le choix le plus sûr est de ne pas poursuivre son trajet à moto.</strong> Ne vous fiez pas uniquement à l'aspect de la chaussée ou à la température affichée au tableau de bord.",
        },
      ],
    },
    {
      part: 'Partie 7 — La visibilité',
      title: 'Être visible : un enjeu majeur en hiver',
      intro:
        "En hiver, la luminosité diminue plus tôt et les conditions météo peuvent réduire la perception des autres usagers. <strong>Être vu devient aussi important que voir.</strong>",
      body:
        "<p>Pour améliorer votre visibilité :</p><ul><li>Vérifiez régulièrement le fonctionnement et la propreté des feux.</li><li>Portez un équipement comportant des éléments visibles, notamment dans des conditions de faible luminosité.</li><li>Gardez votre visière propre et suffisamment transparente.</li><li>Adaptez votre allure pour disposer du temps nécessaire à l'observation.</li><li>Anticipez les situations dans lesquelles les autres conducteurs pourraient vous voir tardivement.</li></ul>",
      callouts: [
        {
          icon: Eye,
          accent: '#0ea5e9',
          body: "<strong>Être visible ne garantit pas d'être vu.</strong> Continuez donc à surveiller les intersections, les changements de file et les véhicules susceptibles de vous couper la route.",
        },
      ],
    },
    {
      part: 'Partie 8 — Renoncer',
      title: 'Savoir renoncer : une véritable compétence de motard',
      intro:
        "Un motard expérimenté n'est pas celui qui roule quelles que soient les conditions. <strong>C'est celui qui sait évaluer les risques et adapter sa décision.</strong>",
      orderedList: [
        "<strong>La route est-elle praticable ?</strong> Vérifiez la présence de neige, de verglas et les éventuelles restrictions de circulation.",
        "<strong>Suis-je correctement équipé ?</strong> Mes mains, mes pieds et mon corps sont-ils protégés sans que mes mouvements soient gênés ?",
        "<strong>Puis-je conserver une marge de sécurité suffisante ?</strong> Si la visibilité est trop faible, si le vent est trop fort ou si l'adhérence est incertaine, mieux vaut reporter le déplacement.",
      ],
      callouts: [
        {
          icon: HelpCircle,
          accent: '#f59e0b',
          body: "<strong>Savoir renoncer ne constitue pas un échec.</strong> C'est une décision responsable qui fait partie intégrante de la maîtrise du risque.",
        },
      ],
    },
    {
      part: 'À retenir',
      title: 'La check-list Code2Roues avant chaque trajet hivernal',
      cards: [
        { icon: Snowflake, accent: '#0ea5e9', kicker: '1', title: 'Météo & routes', body: "Consulter températures, précipitations, alertes et état des chaussées." },
        { icon: Target, accent: '#0ea5e9', kicker: '2', title: 'Pneumatiques', body: "Pression à froid, sculptures, état général. Adapter aux conditions." },
        { icon: ShieldCheck, accent: '#0ea5e9', kicker: '3', title: 'Moto & équipement', body: "Freins, feux, commandes vérifiés avant chaque sortie." },
        { icon: Thermometer, accent: '#0ea5e9', kicker: '4', title: 'Vêtements adaptés', body: "Gants, bottes, couches techniques, protection vent et pluie." },
        { icon: Eye, accent: '#0ea5e9', kicker: '5', title: 'Visibilité', body: "Visière propre, antibuée, feux fonctionnels, équipement visible." },
        { icon: Clock, accent: '#0ea5e9', kicker: '6', title: 'Marge de sécurité', body: "Plus de temps, plus de distance, plus de douceur sur commandes." },
        { icon: HelpCircle, accent: '#0ea5e9', kicker: '7', title: 'Savoir renoncer', body: "Reporter le trajet si les conditions deviennent dangereuses." },
      ],
      cardsCols: 4,
      callouts: [
        {
          icon: Sparkles,
          accent: '#0ea5e9',
          body: "<strong>En hiver, la sécurité commence avant le départ.</strong> Adaptez votre allure, regardez loin, gardez vos distances et privilégiez toujours la sécurité à la performance. Lorsque les conditions ne permettent plus de rouler sereinement, choisissez de reporter votre trajet.",
        },
      ],
    },
  ],
  ctaFinal: {
    kicker: 'Pratique guidée',
    title: 'Préparez votre saison d\'hiver avec un enseignant qualifié',
    body: "Rouler l'hiver demande <strong>des automatismes qui se travaillent</strong> : freinage progressif, lecture de chaussée, trajectoires sécurisantes. Nos dossiers <strong>Death Grip</strong>, <strong>Freinage d'urgence</strong> et <strong>Équilibre à moto</strong> complètent cette approche saisonnière.",
    primary: { label: 'Lire le dossier Freinage', to: '/magazine/freinage-urgence-moto' },
    secondary: { label: 'Voir le dossier Death Grip', to: '/magazine/death-grip-moto-crispation-guidon' },
  },
  sources: [
    { label: 'Ministère de la Transition écologique — Les bons réflexes sur la route en hiver' },
    { label: 'Sécurité routière — Conseils aux motards en automne et en hiver' },
    { label: 'Service-Public.fr — Les équipements obligatoires à moto' },
  ],
};
